/**
 * Shared constants for Hero Section (Desktop + Mobile)
 * Single source of truth - changes here update both platforms
 */

import { isCryptoAsset } from '@/shared/components/platform/utils';

// Logo imports
import tradingviewLogoLight from "@/assets/logos/tradingview-logo-light.svg";
import polygonLogoLight from "@/assets/logos/Polygon.io/massive-logo-white (1).svg";
import openaiLogoWhite from "@/assets/logos/openai-logo-white.svg";
import anthropicLogoWhite from "@/assets/logos/anthropic-logo-white.svg";
import googleCloudLogo from "@/assets/logos/google-cloud-logo.svg";
import difcLogoWhite from "@/assets/logos/difc-logo-white.svg";
import claudeLogo from "@/assets/logos/claude-logo-white.svg";
import googleGeminiLogo from "@/assets/logos/google-gemini-logo.svg";
import dubaiAICampusLogo from "@/assets/logos/Dubai AI Campus/Dubai AI Campus_idYr8RR4Sx_0.svg";
import ibkrLogo from "@/assets/logos/Supported Brokers/Interactive_Brokers_Logo_(2014).svg";
import alpacaLogo from "@/assets/logos/Supported Brokers/Alpaca.svg";
import schwabLogo from "@/assets/logos/Supported Brokers/Charles_Schwab_Corporation_logo.svg";
import coinbaseLogo from "@/assets/logos/Supported Brokers/Coinbase.svg";
import binanceLogo from "@/assets/logos/Supported Brokers/Binance_logo.svg";
import cryptoComLogo from "@/assets/logos/Supported Brokers/Crypto_com_logo.svg";
import krakenLogo from "@/assets/logos/Supported Brokers/K-logo-wikipedia.svg";
import okxLogo from "@/assets/logos/Supported Brokers/OKX_logo.svg";
import capitalComLogo from "@/assets/logos/Supported Brokers/Capital_com_logo.svg";

// ============================================
// HERO HEADLINE - Single source of truth
// ============================================
export const HERO_HEADLINE = {
  en: {
    line1: "You Say It.",
    brand: "MiDash",
    line2: "",
  },
  ar: {
    line1: "فقط قلها,",
    brand: "MiDash",
    line2: "",
  },
};

// ============================================
// PARTNER LOGOS - Technology & AI Partners
// Dimensions match CSS in hero-immersive.desktop.css for CLS prevention
// ============================================
export const PARTNER_LOGOS = [
  { short: "TV", name: "TradingView", logo: tradingviewLogoLight, width: 140, height: 20 },
  { short: "DIFC", name: "DIFC", logo: difcLogoWhite, width: 260, height: 38 },
  { short: "OPENAI", name: "OpenAI", logo: openaiLogoWhite, width: 150, height: 22 },
  { short: "POLY", name: "Massive", logo: polygonLogoLight, width: 115, height: 17 },
  { short: "ANTH", name: "Anthropic", logo: anthropicLogoWhite, width: 105, height: 15 },
  { short: "GCLOUD", name: "Google Cloud", logo: googleCloudLogo, width: 150, height: 22 },
  { short: "CLAUDE", name: "Claude", logo: claudeLogo, width: 130, height: 19 },
  { short: "DAIC", name: "Dubai AI Campus", logo: dubaiAICampusLogo, width: 150, height: 22 },
  { short: "GEMINI", name: "Google Gemini", logo: googleGeminiLogo, width: 150, height: 22 },
];

// ============================================
// BROKER LOGOS - Supported Brokers
// ============================================
export const BROKER_LOGOS = [
  { short: "IBKR", name: "Interactive Brokers", logo: ibkrLogo },
  { short: "ALP", name: "Alpaca", logo: alpacaLogo },
  { short: "SCHW", name: "Charles Schwab", logo: schwabLogo },
  { short: "CBASE", name: "Coinbase", logo: coinbaseLogo },
  { short: "BNC", name: "Binance", logo: binanceLogo },
  { short: "CDC", name: "Crypto.com", logo: cryptoComLogo },
  { short: "KRKN", name: "Kraken", logo: krakenLogo },
  { short: "OKX", name: "OKX", logo: okxLogo },
  { short: "CAP", name: "Capital.com", logo: capitalComLogo },
];

// ============================================
// SUGGESTION CHIPS - Landing Page Actions
// ============================================
export const SUGGESTION_CHIPS = [
  { label: "Tour", prompt: "Give me a tour of what MiDash can do" },
  { label: "Build Portfolio", prompt: "Build me a moderate-risk portfolio" },
  { label: "TSLA + Stop", prompt: "Put 10% of my portfolio in TSLA with a $2 stop loss" },
  { label: "What's Moving?", prompt: "What's moving in the market today?" },
  { label: "Crypto", prompt: "What's hot in crypto right now?" },
  { label: "Analyze AAPL", prompt: "Analyze Apple and suggest entry points" },
];

// ============================================
// GAUGE THRESHOLDS - Market Indicator Colors
// ============================================
export const GAUGE_THRESHOLDS = [
  { max: 40, color: 'var(--color-negative)', gradientFrom: 'var(--color-negative)', gradientTo: 'var(--color-negative)', trail: 'var(--color-negative-soft)' },
  { max: 60, color: 'var(--color-warning)', gradientFrom: 'var(--color-warning)', gradientTo: 'var(--color-warning)', trail: 'var(--color-warning-soft)' },
  { max: 100, color: 'var(--color-positive)', gradientFrom: 'var(--color-accent-deep, #3f9da1)', gradientTo: 'var(--color-positive)', trail: 'var(--color-positive-soft)' },
];

// ============================================
// MOCK CHAT HISTORY - Demo UI Data
// ============================================
export const MOCK_CHAT_HISTORY = [
  { id: "1", title: "BTC Price Analysis", date: "Today, 2:30 PM", preview: "What's Bitcoin trading at?" },
  { id: "2", title: "Portfolio Allocation", date: "Today, 11:15 AM", preview: "Allocate 60% stocks, 30% bonds..." },
  { id: "3", title: "Tech Stock Scan", date: "Yesterday", preview: "Find undervalued tech stocks" },
  { id: "4", title: "Market Overview", date: "Dec 19", preview: "Show me the market overview" },
];

export const ARABIC_CHAT_HISTORY_FALLBACKS: Record<string, { title: string; date: string; preview: string }> = {
  "1": { title: "تحليل سعر بيتكوين", date: "اليوم، 2:30 م", preview: "بكم يتداول بيتكوين الآن؟" },
  "2": { title: "توزيع المحفظة", date: "اليوم، 11:15 ص", preview: "وزّع 60% أسهم، 30% سندات..." },
  "3": { title: "فحص أسهم التقنية", date: "أمس", preview: "ابحث عن أسهم تقنية منخفضة التقييم" },
  "4": { title: "نظرة على السوق", date: "19 ديسمبر", preview: "اعرض لي نظرة عامة على السوق" },
};

// ============================================
// BLINKING CURSOR - Unified across all inputs
// ============================================
export const CURSOR_STYLES = {
  width: '2px',
  height: '18px',
  color: 'var(--color-accent)',
  animationDuration: '1s',
  // Position offsets (from left edge of input)
  desktopLeft: '64px',
  mobileHeroLeft: '48px',
  mobileImmersiveLeft: '18px',
};

// ============================================
// TIME-BASED GREETING
// ============================================
export const getTimeBasedGreeting = (): { key: string; fallback: string } => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) {
    return { key: "greetings.morning", fallback: "Good morning" };
  } else if (hour >= 12 && hour < 17) {
    return { key: "greetings.afternoon", fallback: "Good afternoon" };
  } else if (hour >= 17 && hour < 21) {
    return { key: "greetings.evening", fallback: "Good evening" };
  } else {
    return { key: "greetings.night", fallback: "Good evening" };
  }
};

// ============================================
// THEME UTILITIES
// ============================================
export const stripHtml = (value: string): string => value.replace(/<[^>]*>/g, "");

export const getInitialTheme = (): "dark" | "light" => {
  if (typeof window === "undefined") return "light";
  const stored = localStorage.getItem("theme");
  if (stored === "light" || stored === "dark") return stored;
  // Default to light mode for new visitors — matches the head bootstrap
  // in index.html that writes 'light' to localStorage on first visit.
  // System preference is intentionally ignored: the brand defaults to
  // light regardless of OS theme so first-time visitors land on the
  // intended product surface.
  return "light";
};

export const applyTheme = (nextTheme: "dark" | "light"): void => {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("light", nextTheme === "light");
  document.documentElement.style.colorScheme = nextTheme;
};

// ============================================
// TRADINGVIEW SYMBOL MAPPING
// ============================================
export const getTradingViewSymbol = (symbol: string, assetType: 'stock' | 'crypto' | 'forex' | string): string => {
  // Use isCryptoAsset() for case-insensitive matching (handles "crypto", "CRYPTO", "SPOT", etc.)
  if (isCryptoAsset(assetType)) {
    // Strip Polygon.io "X:" prefix (e.g., "X:BTCUSD" → "BTCUSD")
    let cleanSymbol = symbol;
    if (cleanSymbol.startsWith('X:')) cleanSymbol = cleanSymbol.slice(2);
    // Strip "USD"/"USDT" suffix to get base symbol (e.g., "BTCUSD" → "BTC")
    const base = cleanSymbol.replace(/USD[T]?$/i, '');

    const cryptoMap: Record<string, string> = {
      'BTC': 'BINANCE:BTCUSDT',
      'ETH': 'BINANCE:ETHUSDT',
      'SOL': 'BINANCE:SOLUSDT',
      'XRP': 'BINANCE:XRPUSDT',
      'DOGE': 'BINANCE:DOGEUSDT',
      'ADA': 'BINANCE:ADAUSDT',
      'AVAX': 'BINANCE:AVAXUSDT',
      'LINK': 'BINANCE:LINKUSDT',
    };
    return cryptoMap[base] || `BINANCE:${base}USDT`;
  }

  // Forex and commodities
  if (assetType === 'forex') {
    // Commodity mappings (metals, energy) - use TVC for indices
    const commodityMap: Record<string, string> = {
      'XAUUSD': 'TVC:GOLD',
      'XAGUSD': 'TVC:SILVER',
      'WTIUSD': 'TVC:USOIL',
      'NGAS': 'TVC:NATGAS',
      'COPPER': 'TVC:COPPER',
      'PLATINUM': 'OANDA:XPTUSD',
    };
    if (commodityMap[symbol]) {
      return commodityMap[symbol];
    }
    // Forex pairs (EURUSD, GBPUSD, etc.)
    return `FX:${symbol}`;
  }

  // Stocks - use NASDAQ for most tech stocks
  const exchangeMap: Record<string, string> = {
    'AAPL': 'NASDAQ:AAPL',
    'NVDA': 'NASDAQ:NVDA',
    'TSLA': 'NASDAQ:TSLA',
    'AMZN': 'NASDAQ:AMZN',
    'GOOGL': 'NASDAQ:GOOGL',
    'META': 'NASDAQ:META',
    'MSFT': 'NASDAQ:MSFT',
    'AMD': 'NASDAQ:AMD',
    'SPY': 'AMEX:SPY',
    'QQQ': 'NASDAQ:QQQ',
    // Commodity ETFs (NYSE Arca)
    'USO': 'AMEX:USO',
    'UNG': 'AMEX:UNG',
  };
  // For unknown symbols, let TradingView auto-resolve (no NASDAQ default)
  return exchangeMap[symbol] || symbol;
};

// ============================================
// TOOLBAR TABS - Sidebar Navigation Groups
// ============================================

// Primary navigation (no section header)
export const TOOL_PRIMARY = [
  { key: "overview", fallback: "Overview" },
  { key: "charts", fallback: "Charts" },
  { key: "chats", fallback: "Chats" },
];

// Agents Lab section (Trading Agents, Scanning, Backtesting, Alerts)
// The Backtesting slot moved from key "trading" (the retired codex chat) to
// "backtesting", which renders BacktestHistoryPanel — the agents-runtime
// backtest space that doc 40 built to replace the codex chat in this slot.
export const TOOL_AGENT_LAB = [
  { key: "agents", fallback: "Trading Agents" },
  { key: "scanning", fallback: "Scanning" },
  { key: "backtesting", fallback: "Backtesting" },
  { key: "alerts", fallback: "Alerts" },
];

// Indicators section (separate, at the end)
export const TOOL_INDICATORS = [
  { key: "stocks", fallback: "Stocks" },
  { key: "crypto", fallback: "Crypto" },
];

// Section metadata for sidebar labels
export const SIDEBAR_SECTIONS = {
  agentsLab: {
    labelKey: "platform.sections.agentsLab",
    fallback: "Agents Lab",
  },
  indicators: {
    labelKey: "platform.sections.indicators",
    fallback: "Indicators",
  },
};

// ============================================
// SCANNER PREVIEW CONSTANTS
// ============================================
export const SCANNER_PREVIEW_TABS = [
  { key: "scan", labelKey: "platform.scanner.preview.tabs.scan", fallback: "Scan", active: true },
  { key: "results", labelKey: "platform.scanner.preview.tabs.results", fallback: "Results" },
  { key: "saved", labelKey: "platform.scanner.preview.tabs.saved", fallback: "Saved Scans" },
];

export const SCANNER_PREVIEW_FILTERS = [
  { key: "volume", labelKey: "platform.scanner.preview.filters.volume", fallback: "Volume > 1M" },
  { key: "rsi", labelKey: "platform.scanner.preview.filters.rsi", fallback: "RSI < 30" },
  { key: "price", labelKey: "platform.scanner.preview.filters.price", fallback: "Price > $10" },
  { key: "change", labelKey: "platform.scanner.preview.filters.change", fallback: "Change > 5%" },
];

export const SCANNER_PREVIEW_RESULTS = [
  { key: "nvda", symbol: "NVDA", name: "NVIDIA", change: "+8.4%", volume: "52M", tone: "positive" },
  { key: "tsla", symbol: "TSLA", name: "Tesla", change: "+5.2%", volume: "38M", tone: "positive" },
  { key: "amd", symbol: "AMD", name: "AMD", change: "+4.1%", volume: "28M", tone: "positive" },
];

// ============================================
// LAB/TESTING PREVIEW CONSTANTS
// ============================================
export const LAB_PREVIEW_TABS = [
  { key: "create", labelKey: "platform.lab.preview.tabs.create", fallback: "Create Agent", active: true },
  { key: "saved", labelKey: "platform.lab.preview.tabs.saved", fallback: "Saved Agents" },
  { key: "deployed", labelKey: "platform.lab.preview.tabs.deployed", fallback: "Deployed Agents" },
  { key: "monitor", labelKey: "platform.lab.preview.tabs.monitor", fallback: "Agent Monitor" },
  { key: "templates", labelKey: "platform.lab.preview.tabs.templates", fallback: "Agent Templates" },
];

export const LAB_PREVIEW_CONTROLS = [
  { key: "risk", labelKey: "platform.lab.preview.controls.risk", fallback: "Max risk 2%" },
  { key: "stop", labelKey: "platform.lab.preview.controls.stop", fallback: "Stop loss 1.5%" },
  { key: "take", labelKey: "platform.lab.preview.controls.take", fallback: "Take profit 4%" },
  { key: "session", labelKey: "platform.lab.preview.controls.session", fallback: "US session only" },
];

export const LAB_PREVIEW_STEPS = [
  { key: "interpret", labelKey: "platform.lab.preview.steps.interpret", fallback: "AI interprets your strategy" },
  { key: "backtest", labelKey: "platform.lab.preview.steps.backtest", fallback: "Run 5-year backtest simulation" },
  { key: "deploy", labelKey: "platform.lab.preview.steps.deploy", fallback: "Deploy with paper trading" },
];

export const LAB_PREVIEW_METRICS = [
  { key: "winRate", labelKey: "platform.lab.preview.metrics.winRate", fallback: "Win rate", value: "62%" },
  { key: "return", labelKey: "platform.lab.preview.metrics.return", fallback: "Return", value: "+18.4%" },
  { key: "drawdown", labelKey: "platform.lab.preview.metrics.drawdown", fallback: "Max drawdown", value: "-8.4%" },
  { key: "trades", labelKey: "platform.lab.preview.metrics.trades", fallback: "Trades", value: "128" },
];

export const LAB_PREVIEW_RUNS = [
  { key: "momentum", name: "Momentum Pulse", status: "Simulating", pnl: "+4.2%", updated: "2m ago" },
  { key: "mean", name: "Mean Reversion", status: "Queued", pnl: "+1.8%", updated: "5m ago" },
  { key: "trend", name: "Trend Breakout", status: "Completed", pnl: "+7.6%", updated: "1h ago" },
];

// ============================================
// ALERTS PREVIEW CONSTANTS
// ============================================
export const ALERTS_PREVIEW_TABS = [
  { key: "create", labelKey: "platform.alerts.preview.tabs.create", fallback: "Create Alert", active: true },
  { key: "active", labelKey: "platform.alerts.preview.tabs.active", fallback: "Active Alerts" },
  { key: "rules", labelKey: "platform.alerts.preview.tabs.rules", fallback: "Alert Rules" },
];

export const ALERTS_PREVIEW_TYPES = [
  { key: "price", labelKey: "platform.alerts.preview.types.price", fallback: "Price cross" },
  { key: "rsi", labelKey: "platform.alerts.preview.types.rsi", fallback: "RSI signal" },
  { key: "volume", labelKey: "platform.alerts.preview.types.volume", fallback: "Volume spike" },
  { key: "news", labelKey: "platform.alerts.preview.types.news", fallback: "News event" },
];

export const ALERTS_PREVIEW_RULES = [
  { key: "nvda", labelKey: "platform.alerts.preview.rules.nvda", fallback: "NVDA above $500" },
  { key: "spy", labelKey: "platform.alerts.preview.rules.spy", fallback: "SPY drops 1.2% intraday" },
  { key: "btc", labelKey: "platform.alerts.preview.rules.btc", fallback: "BTC volume 2x 30D avg" },
];

export const ALERTS_PREVIEW_ACTIVE = [
  { key: "alpha", name: "Macro Break", detail: "DXY breaks 104.50", status: "Armed" },
  { key: "gamma", name: "Earnings Watch", detail: "TSLA -20% implied move", status: "Armed" },
  { key: "delta", name: "Crypto Flow", detail: "ETH funding flip", status: "Paused" },
];

export const ALERTS_PREVIEW_FEED = [
  { key: "fed", title: "Fed Watch", detail: "Rates speech in 15m", tone: "info" },
  { key: "nvda", title: "NVDA Alert", detail: "Crossed above $500", tone: "positive" },
  { key: "spy", title: "SPY Alert", detail: "Volatility spike", tone: "warning" },
];
